import classnames from 'classnames/bind'
import {
  ReadonlyURLSearchParams,
  usePathname,
  useSearchParams,
} from 'next/navigation'
import { useInView } from 'react-intersection-observer'
import { useTranslate } from 'react-polyglot'

import { Spinner } from '@unlikelystudio/react-abstract-components'
import { useIsomorphicLayoutEffect } from '@unlikelystudio/react-hooks'

import SquaredCta from '~/components/UI/SquaredCta'

import { useSearchAndDiscoveryContext } from '~/providers/SearchAndDiscoveryProvider'

import useLocale from '~/hooks/useLocale'

import { applySearchParams } from '~/utils/apply-searchparams'

import { GENERAL } from '~/data/dictionary'

import css from './styles.module.scss'
import { useTransition } from 'react'

const cx = classnames.bind(css)

type LoadMoreProps = {
  className?: string
  hasNextPage: boolean
  fetchNextPage: () => void
  isFetchingNextPage: boolean
}

function buildHref({
  page,
  pathname,
  searchParams: readOnlySearchParams,
  locale,
}: {
  page: number
  pathname: string
  searchParams: ReadonlyURLSearchParams
  locale: string
}) {
  const searchParams = new URLSearchParams(readOnlySearchParams?.toString())

  searchParams.set('page', String(page))

  return applySearchParams(`/${locale}${pathname}`, searchParams)
}

export function ClickLoadMore({
  className,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
}: LoadMoreProps) {
  const t = useTranslate()
  const { page, setPage } = useSearchAndDiscoveryContext()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const [isPending, startTransition] = useTransition()

  const onClick = () => {
    startTransition(() => {
      if (!isFetchingNextPage) {
        fetchNextPage()
        setPage(page + 1)
      }
    })
  }

  return hasNextPage ? (
    <div className={cx(css.buttonWrapper, className)}>
      <SquaredCta
        href={buildHref({
          page: page + 1,
          pathname,
          searchParams,
          locale,
        })}
        onClick={onClick}
        className={css.loadMore}
        isLoading={isFetchingNextPage || isPending}>
        {t(GENERAL.LOAD_MORE)}
      </SquaredCta>
    </div>
  ) : null
}

export function InfiniteLoadMore({
  className,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
}: LoadMoreProps) {
  const [inViewRef, inView] = useInView({
    rootMargin: '500px',
  })
  const { page, setPage } = useSearchAndDiscoveryContext()

  const t = useTranslate()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const locale = useLocale()

  const onLoadMore = () => {
    if (!isFetchingNextPage) {
      fetchNextPage()
      setPage(page + 1)
    }
  }

  useIsomorphicLayoutEffect(() => {
    if (inView && hasNextPage) onLoadMore()
  }, [inView, hasNextPage])

  return hasNextPage ? (
    <div className={className} ref={inViewRef}>
      {isFetchingNextPage && (
        <div className={css.spinnerWrapper}>
          <Spinner className={css.spinner} />
        </div>
      )}

      <a
        className={css.hiddenLoadMore}
        href={buildHref({
          page: page + 1,
          pathname,
          searchParams,
          locale,
        })}>
        {t(GENERAL.LOAD_MORE)}
      </a>
    </div>
  ) : null
}
